<template>
  <div class="detail-view">
    <!-- Mobile Header -->
    <MobileHeader
      v-if="isMobile"
      :lottery="lottery"
      :countdown="countdown"
      @go-back="router.back()"
    />

    <!-- Desktop Header -->
    <DesktopHeader
      v-else-if="!isMobile && lottery"
      :lottery="lottery"
      :countdown="countdown"
      @go-back="router.back()"
    />

    <div class="content-layout">
      <div class="main-content-wrapper">

        <!-- Mobile Draws Section -->
        <MobileDrawsSection
          v-if="isMobile"
          :draws="gameStore.getValidDraws"
          :selected-draws="selectedDrawsArray"
          @update-selected-draws="handleUpdateSelectedDraws"
          @toggle-draw-selection="toggleDrawSelection"
        />

        <!-- Desktop Draws Section -->
        <DesktopDrawsSection
          v-else
          :draws="gameStore.getValidDraws"
          :selected-draws="selectedDrawsArray"
          @update-selected-draws="handleUpdateSelectedDraws"
          @toggle-draw-selection="toggleDrawSelection"
        />

        <!-- Related Games Selector -->
        <div
          v-if="lottery && hasRelatedGamesForLottery"
          class="related-games-inline-wrapper"
        >
          <div class="related-games-header">
            <span class="related-games-title">Also add to:
            </span>
          </div>
          <RelatedGamesSelector
            :current-game-slug="lottery.slug"
            :selected-games="selectedRelatedGames"
            :is-processing="isPlacingBet"
            @update:selected-games="selectedRelatedGames = $event"
          />
        </div>

        <!-- Mobile Betting Section -->
        <MobileBettingSection
          v-if="isMobile"
          :key="`mobile-betting-${isMobile}`"
          :betting-markets="bettingMarkets"
          :selected-market="selectedMarket"
          :hot-numbers="gameStore.getHotNumbers"
          :cold-numbers="gameStore.getColdNumbers"
          :betslip-length="betslip.length"
          @select-market="selectMarket"
          @remove-market="removeMarket"
          @toggle-group="toggleGroup"
          @tab-changed="onMobileTabChanged"
          @add-line="addLine"
          @remove-line="removeLine"
          @add-to-betslip="(mobileLines) => addToBetslip(mobileLines)"
          @clear-all="clearAllLines"
        />

        <!-- Desktop Betting Section -->
        <DesktopBettingSection
          v-if="!isMobile"
          :key="`desktop-betting-${!isMobile}`"
          :betting-markets="bettingMarkets"
          :selected-market="selectedMarket"
          :hot-numbers="gameStore.getHotNumbers"
          :cold-numbers="gameStore.getColdNumbers"
          :betslip-length="betslip.length"
          @select-market="selectMarket"
          @remove-market="removeMarket"
          @toggle-group="toggleGroup"
          @tab-changed="onMobileTabChanged"
          @add-line="addLine"
          @remove-line="removeLine"
          @add-to-betslip="(desktopLines) => addToBetslip(desktopLines)"
          @clear-all="clearAllLines"
        />

        <!-- Mobile Confirm Section -->
        <MobileConfirmSection
          v-if="isMobile && selectedMarket && selectedMarket.select_numbers && mobileActiveTab === 'main'"
          :key="`mobile-confirm-${isMobile}`"
          :lines="lines"
          :current-game-slug="lottery?.slug"
          :selected-related-games="selectedRelatedGames"
          :is-processing="isPlacingBet"
          @clear-all="clearAllLines"
          @remove-line="removeLine"
          @add-to-betslip="addToBetslip"
          @update:selected-related-games="selectedRelatedGames = $event"
        />

        <!-- Desktop Confirm Section -->
        <DesktopConfirmSection
          v-if="!isMobile && selectedMarket && selectedMarket.select_numbers"
          :key="`desktop-confirm-${!isMobile}`"
          :lines="lines"
          :current-game-slug="lottery?.slug"
          :selected-related-games="selectedRelatedGames"
          :is-processing="isPlacingBet"
          @clear-all="clearAllLines"
          @remove-line="removeLine"
          @add-to-betslip="addToBetslip"
          @update:selected-related-games="selectedRelatedGames = $event"
        />
        <!-- Mobile View Bets Button - Always visible on mobile when there are bets -->
        <div v-if="isMobile" class="mobile-view-bets-container" ref="mobileViewBetsRef">
          <button
            class="mobile-view-bets"
            @click="openBetslip"
          >
            View Bets ({{ betslip.length }})
          </button>
        </div>
        
        <!-- Mobile Back Button - Always visible on mobile -->
        <div v-if="isMobile" class="mobile-back-container">
          <button
            class="mobile-back-button"
            @click="goBackToLotteries"
          >
            <svg class="mobile-back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span class="mobile-back-text">BACK TO LOTTERIES</span>
          </button>
        </div>
      </div>
      <div class="betslip-wrapper" ref="betslipWrapperRef">
        <BetSlip
          @remove-bet="removeBetFromSlip"
          @clear="clearBetslip"
          @clearedAfterSubmission="handleClearedAfterSubmission"
          @selectDraws="handleSelectDraws"
          @drawsChanged="handleDrawsChangedFromBetSlip"
        />
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, defineEmits, defineExpose, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useBetslipStore } from "../stores/betslip";
import { useGameStore } from "../stores/game";
import { useToast } from "../composables/useToast";
import { useModalStore } from "../stores/modal";
import type { BetslipItem } from "../stores/betslip";
import BetSlip from "../components/BetSlip.vue";
import MobileDrawsSection from "../components/mobile/MobileDrawsSection.vue";
import DesktopDrawsSection from "../components/desktop/DesktopDrawsSection.vue";
import MobileBettingSection from "../components/mobile/MobileBettingSection.vue";
import MobileConfirmSection from "../components/mobile/MobileConfirmSection.vue";
import MobileHeader from "../components/mobile/MobileHeader.vue";
import DesktopHeader from "../components/desktop/DesktopHeader.vue";
import DesktopBettingSection from "../components/desktop/DesktopBettingSection.vue";
import DesktopConfirmSection from "../components/desktop/DesktopConfirmSection.vue";
import RelatedGamesSelector from "../components/RelatedGamesSelector.vue";
import { useRelatedGames } from "../composables/useRelatedGames";
import { useRelatedGameMatching } from "../composables/useRelatedGameMatching";

const route = useRoute();
const router = useRouter();
const gameStore = useGameStore();
const { showToast } = useToast();
const modalStore = useModalStore();
const emit = defineEmits(['openBetslipModal', 'drawsChanged']);

interface Game {
  id: string;
  name: string;
  slug: string;
  flagUrl: string;
  drawTime: Date;
  cutoffTime: Date;
  country: string;
  draw_date?: string;
  cutoff?: string;
  game_name?: string;
  game_slug?: string;
  game_logo?: string;
}

interface MarketOutcome {
  id: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
  specifier: string | null;
}

interface Market {
  id: number;
  name: string;
  slug: string;
  description: string;
  ordering: number;
  outcomes: MarketOutcome[];
}

interface SelectedMarket {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}

interface MarketGroup {
  id: number;
  name: string;
  slug: string;
  ordering: number;
  has_bonus: boolean;
  markets: Market[];
  rules: Array<{
    id: number;
    name: string;
    balls_min: number;
    balls_max: number;
    picks_min: number;
    picks_max: number;
    has_bonus: number | boolean;
    max_resulted: number;
    type: string;
  }>;
}

interface Line {
  numbers: number[];
  bonus?: number;
  numBets: number;
  picks: number;
  market?: SelectedMarket;
}

const lottery = ref<Game | null>(null);
const selectedDrawsArray = ref<boolean[]>([]);
const selectedMarket = ref<SelectedMarket | null>(null);
const mobileActiveTab = ref<'main' | 'bonus'>('main');
const isMobile = ref(window.innerWidth <= 768);
const lines = ref<Line[]>([]);
const bettingMarkets = ref<MarketGroup[]>([]);
const mobileViewBetsRef = ref<HTMLElement | null>(null);
const betslipWrapperRef = ref<HTMLElement | null>(null);
const isClearingAfterSubmission = ref(false);
const lastAddedBet = ref<BetslipItem | null>(null);
const selectedRelatedGames = ref<string[]>([]);
const { getRelatedGamesSync } = useRelatedGames();
const { findMatchingBet, createRelatedBet } = useRelatedGameMatching();

const hasRelatedGamesForLottery = computed(() => {
  if (!lottery.value) return false;
  const related = getRelatedGamesSync(lottery.value.slug);
  return Array.isArray(related) && related.length > 0;
});

const countdown = ref({
  hours: 0,
  minutes: 0,
  seconds: 0
});

const updateCountdown = () => {
  const validDraws = gameStore.getValidDraws;
  if (!validDraws || validDraws.length === 0) {
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
    return;
  }

  const earliestDraw = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    const earliestTime = new Date(earliest.cutoff || earliest.draw_date);
    return cutoffTime < earliestTime ? draw : earliest;
  }, validDraws[0]);
  
  try {
    const now = new Date();
    const cutoffRaw = earliestDraw.cutoff || earliestDraw.draw_date;
    
    let cutoffTime: Date;
    if (typeof cutoffRaw === 'string') {
      const dateStr = cutoffRaw.includes('T') ? cutoffRaw : cutoffRaw.replace(' ', 'T');
      cutoffTime = new Date(dateStr);
    } else {
      cutoffTime = new Date(cutoffRaw);
    }
    
    if (isNaN(cutoffTime.getTime())) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }
    
    const diff = cutoffTime.getTime() - now.getTime();

    if (diff <= 0) {
      countdown.value = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    countdown.value = {
      hours: Math.floor(diff / (1000 * 60 * 60)),
      minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((diff % (1000 * 60)) / 1000)
    };
  } catch (error) {
    console.error('Error updating countdown:', error);
    countdown.value = { hours: 0, minutes: 0, seconds: 0 };
  }
};

let countdownTimer: number;

const betslipStore = useBetslipStore();
const { addBet, removeBet: removeBetFromSlip, clearBetslip } = betslipStore;
const betslip = computed(() => betslipStore.betslip);

watch(
  () => route.params.slug,
  async (newSlug) => {
    if (newSlug) {
      try {
        await gameStore.fetchGameDetails(newSlug as string)
        const gameDetail = gameStore.getGameDetail;
        if (gameDetail) {
          lottery.value = {
            id: gameDetail.id.toString(),
            name: gameDetail.name,
            slug: gameDetail.slug,
            flagUrl: gameDetail.logo,
            drawTime: new Date(gameDetail.current_draw.draw_date),
            cutoffTime: new Date(gameDetail.current_draw.cutoff),
            country: gameDetail.game_type.name,
            draw_date: gameDetail.current_draw.draw_date,
            cutoff: gameDetail.current_draw.cutoff,
            game_name: gameDetail.name,
            game_slug: gameDetail.slug,
            game_logo: gameDetail.logo
          };

          const nextDraws = gameStore.getValidDraws || [];
          selectedDrawsArray.value = new Array(nextDraws.length).fill(false);
          if (nextDraws.length > 0) {
            selectedDrawsArray.value[0] = true;
          }

          const marketGroups = gameStore.getMarketGroups || [];
          bettingMarkets.value = marketGroups.map(group => ({
            id: group.id,
            name: group.name,
            slug: group.slug,
            ordering: group.ordering,
            has_bonus: group.has_bonus,
            rules: group.rules,
            markets: group.markets.map(market => ({
              id: market.id,
              name: market.name,
              slug: market.slug,
              description: market.description,
              ordering: market.ordering,
              outcomes: market.outcomes || []
            }))
          }));
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
        router.push('/next-draws');
      }
    }
  },
  { immediate: true }
);

watch(
  () => route.query,
  () => {
    if (route.params.slug) {
      gameStore.fetchGameDetails(route.params.slug as string)
    }
  },
  { deep: true }
);

watch(
  () => selectedDrawsArray.value,
  (newSelection) => {
    const selectedDraws = gameStore.getValidDraws.filter((_, index) => newSelection[index]);
    emit('drawsChanged', selectedDraws);
  },
  { deep: true }
);

watch(
  () => lottery.value,
  (newLottery) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    if (newLottery?.cutoff) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);

watch(
  () => gameStore.getValidDraws,
  (newDraws) => {
    if (countdownTimer) {
      clearInterval(countdownTimer);
    }
    
    if (newDraws && newDraws.length > 0) {
      updateCountdown();
      countdownTimer = window.setInterval(updateCountdown, 1000);
    }
  },
  { immediate: true }
);


const openBetslip = () => {
  modalStore.openBetslip();
};

const goBackToLotteries = () => {
  if (window.history.length > 1) {
    router.back();
  } else {
    router.push({ name: 'next-draws' });
  }
};

const clearAllLines = () => {
  lines.value = [];
  selectedRelatedGames.value = [];
};

const handleClearedAfterSubmission = () => {
  isClearingAfterSubmission.value = true;
  lines.value = [];
  mobileActiveTab.value = 'main';
  
  const nextDraws = gameStore.getValidDraws || [];
  selectedDrawsArray.value = new Array(nextDraws.length).fill(false);
  if (nextDraws.length > 0) {
    selectedDrawsArray.value[0] = true;
  }
};

const scrollToViewBetsButton = () => {
  if (isMobile.value && mobileViewBetsRef.value) {
    mobileViewBetsRef.value.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
  }
};

const scrollToBetslip = () => {
  if (!isMobile.value && betslipWrapperRef.value) {
    betslipWrapperRef.value.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }
};

const selectDraws = (drawCount: number) => {
  const availableDraws = gameStore.getValidDraws || [];
  const maxDraws = Math.min(availableDraws.length, drawCount);
  
  selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
  
  for (let i = 0; i < maxDraws; i++) {
    selectedDrawsArray.value[i] = true;
  }
};

const handleSelectDraws = (drawData: {
  betIndex: number;
  drawCount: number;
  selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>;
  gameId: string | number;
}) => {
  if (betslip.value.length === 0 || isClearingAfterSubmission.value) {
    return;
  }
  
  const currentGameId = gameStore.getGameDetail?.id;
  if (currentGameId && drawData.gameId === currentGameId) {
    const availableDraws = gameStore.getValidDraws || [];
    selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
    
    for (let i = 0; i < Math.min(drawData.drawCount, availableDraws.length); i++) {
      selectedDrawsArray.value[i] = true;
    }
  }
};

const handleDrawsChangedFromBetSlip = (selectedDraws: Array<{draw_date: string, cutoff: string, event_id: number}>) => {
  if (betslip.value.length === 0 || isClearingAfterSubmission.value) {
    return;
  }
  
  const availableDraws = gameStore.getValidDraws || [];
  selectedDrawsArray.value = new Array(availableDraws.length).fill(false);
  
  selectedDraws.forEach(draw => {
    const index = availableDraws.findIndex(availableDraw => 
      availableDraw.event_id === draw.event_id || 
      availableDraw.draw_date === draw.draw_date
    );
    if (index !== -1) {
      selectedDrawsArray.value[index] = true;
    }
  });
};

const toggleDrawSelection = (index: number) => {
  selectedDrawsArray.value[index] = !selectedDrawsArray.value[index];
};

const handleUpdateSelectedDraws = (index: number, checked: boolean) => {
  selectedDrawsArray.value[index] = checked;
};

const expandedGroupIndex = ref<number | null>(0);
const toggleGroup = (idx: number) => {
  expandedGroupIndex.value = expandedGroupIndex.value === idx ? null : idx;
};

const onMobileTabChanged = (tab: 'main' | 'bonus') => {
  mobileActiveTab.value = tab;
};

const selectMarket = async (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}) => {
  if (!bet) return;
  
  if (bet.select_numbers) {
    selectedMarket.value = bet;
    if (isMobile.value) {
      mobileActiveTab.value = 'main';
      onMobileTabChanged('main');
    }
  } else {
    selectedMarket.value = null;
    const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
    
    let betType = bet.label;
    let marketDescription = bet.market.description;
    const outcome = bet.market.outcomes.find(o => o.id.toString() === bet.outcome_id);
    if (outcome?.specifier) {
      betType = bet.label.replace('{value}', outcome.specifier);
      marketDescription = bet.market.description.replace('{value}', outcome.specifier);
    }
    
    const betslipItem: BetslipItem = {
      market_id: bet.market.id,
      market_group_id: bet.marketGroup.id,
      game_id: gameStore.getGameDetail?.id || 0,
      game_name: gameStore.getGameDetail?.name,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      numbers: [],
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      betType: betType,
      maxStake: Number(gameStore.getBetSettings?.bet_limit) ?? 10000,
      odds: bet.odds,
      numBets: 1,
      picks: bet.balls,
      outcome_id: bet.outcome_id,
      label: bet.label,
      market_name: bet.market.name,
      market_description: marketDescription,
      specifier: outcome?.specifier || null,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    let duplicateBets = 0;

    const betAdded = addBet(betslipItem);
    
    if (!betAdded) {
      duplicateBets++;
      showToast({
        title: 'Bet Already Added',
        message: 'This bet is already in your betslip.',
        type: 'info',
        duration: 3000
      });
    } else {
      lastAddedBet.value = betslipItem;

      if (selectedRelatedGames.value.length > 0 && lottery.value && hasRelatedGamesForLottery.value) {
        const relatedGames = getRelatedGamesSync(lottery.value.slug);
        const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));

        for (const relatedGame of selectedGames) {
          try {
            const matchingResult = await findMatchingBet(relatedGame, betslipItem);
            const relatedBet = createRelatedBet(matchingResult, betslipItem);
            const added = addBet(relatedBet);
            if (!added) {
              duplicateBets++;
            }
          } catch (error) {
            console.error(`Error adding bet to ${relatedGame.name}:`, error);
          }
        }
      }

      if (duplicateBets > 0) {
        showToast({
          title: 'Duplicate Bets',
          message: `${duplicateBets} bet(s) were already in your betslip and were skipped.`,
          type: 'info',
          duration: 3000
        });
      }

      selectedRelatedGames.value = [];

      modalStore.closeBetslip();
      setTimeout(() => {
        if (isMobile.value) {
          scrollToViewBetsButton();
        } else {
          scrollToBetslip();
        }
      }, 100);
    }
  }
};

const removeMarket = (bet: {
  marketGroup: MarketGroup;
  market: Market;
  label: string;
  odds: number;
  select_numbers: boolean;
  balls: number;
  outcome_id: string;
}) => {
  const betToRemove = betslip.value.find(item => 
    item.outcome_id === bet.outcome_id &&
    item.market_id === bet.market.market_id
  );
  
  if (betToRemove) {
    removeBetFromSlip(betslip.value.indexOf(betToRemove));
    showToast({
      title: 'Bet Removed',
      message: `${bet.label} has been removed from your betslip.`,
      type: 'info',
      duration: 2000
    });
  }
};

const addLine = (line: Line) => {
  if (!line || !selectedMarket.value) {
    console.error("No market selected when adding line or line is invalid");
    return;
  }
  
  const lineWithMarket = {
    ...line,
    market: selectedMarket.value
  };
  lines.value.push(lineWithMarket);
};

const removeLine = (index: number) => {
  lines.value.splice(index, 1);
};

const isPlacingBet = ref(false);

const addToBetslip = async (mobileLines?: Line[]) => {
  if (isPlacingBet.value) return;
  
  const linesToProcess = mobileLines || lines.value;
  
  if (selectedMarket.value === null || linesToProcess.length === 0) {
    console.error("No market or lines selected");
    return;
  }

  if (!selectedDrawsArray.value.some((selected) => selected)) {
    if (gameStore.getValidDraws.length > 0) {
      selectedDrawsArray.value[0] = true;
    } else {
      console.error("No draws available");
      return;
    }
  }

  const selectedDrawDates = gameStore.getValidDraws.filter((_, index) => selectedDrawsArray.value[index]);
  if (!selectedDrawDates.length) {
    console.error("No draw date selected");
    return;
  }

  let duplicateBets = 0;
  const successfullyAddedBets: BetslipItem[] = [];
  let maxBetsReached = false;
  
  for (const line of linesToProcess) {
    if (maxBetsReached) {
      break;
    }

    const lineMarket = line.market || selectedMarket.value;
    if (!lineMarket) {
      console.error("No market information found for line");
      continue;
    }
    
    const isBonusOnly = lineMarket.marketGroup.slug === 'bonus-ball-only-additional-drum' || 
                       lineMarket.marketGroup.slug === 'bonus-ball-only-same-drum';
    const isMainIncludingBonus = lineMarket.marketGroup.slug === 'standard-bet-bonus-from-same-drum';
    const numbersPayload = [
      isBonusOnly ? [] : (Array.isArray(line.numbers) ? line.numbers : []),
      (!isMainIncludingBonus && line.bonus !== undefined && line.bonus !== null) ? [line.bonus] : []
    ];
    const bet = {
      market_id: lineMarket.market.id,
      market_group_id: lineMarket.marketGroup.id,
      game_name: gameStore.getGameDetail?.name,
      game_id: gameStore.getGameDetail?.id || 0,
      numbers: numbersPayload,
      bonus: line.bonus,
      stake: Number(gameStore.getBetSettings?.bet_default) ?? 1,
      draws: selectedDrawDates,
      allAvailableDraws: gameStore.getValidDraws || [],
      drawCount: selectedDrawDates.length,
      betType: lineMarket.label,
      maxStake: Number(gameStore.getBetSettings?.bet_limit) ?? 10000,
        odds: lineMarket.odds,
        picks: isBonusOnly ? 1 : (line.picks || lineMarket.balls),
        numBets: 1,
        label: lineMarket.label,
        market_name: lineMarket.market.name,
        market_description: lineMarket.market.description,
        outcome_id: lineMarket.outcome_id,
      currency_symbol: gameStore.getBetSettings?.currency_symbol,
    };

    const betAdded = addBet(bet);
    if (!betAdded) {
      duplicateBets++;
    } else {
      successfullyAddedBets.push(bet);
      lastAddedBet.value = bet;
    }
  }

  // Process related games for all successfully added bets
  if (selectedRelatedGames.value.length > 0 && successfullyAddedBets.length > 0 && lottery.value) {
    const relatedGames = getRelatedGamesSync(lottery.value.slug);
    const selectedGames = relatedGames.filter(g => selectedRelatedGames.value.includes(g.slug));
    
    // Process each successfully added bet
    for (const addedBet of successfullyAddedBets) {
      // Process each selected related game for this bet
      for (const relatedGame of selectedGames) {
        try {
          const matchingResult = await findMatchingBet(relatedGame, addedBet);
          const relatedBet = createRelatedBet(matchingResult, addedBet);
          const added = addBet(relatedBet);
          if (!added) {
            duplicateBets++;
          }
        } catch (error) {
          console.error(`Error adding bet to ${relatedGame.name}:`, error);
        }
      }
    }
  }
  
  if (duplicateBets > 0) {
    showToast({
      title: 'Duplicate Bets',
      message: `${duplicateBets} bet(s) were already in your betslip and were skipped.`,
      type: 'info',
      duration: 3000
    });
  }

  lines.value = [];
  selectedRelatedGames.value = [];
  mobileActiveTab.value = 'main';
  
  setTimeout(() => {
    if (isMobile.value) {
      scrollToViewBetsButton();
    } else {
      scrollToBetslip();
    }
  }, 100);
};


let drawsRefreshTimer: number | null = null;

const setupDrawRefreshTimer = () => {
  const validDraws = gameStore.getValidDraws;
  if (validDraws.length === 0) return;

  const earliestCutoff = validDraws.reduce((earliest, draw) => {
    const cutoffTime = new Date(draw.cutoff || draw.draw_date);
    return cutoffTime < earliest ? cutoffTime : earliest;
  }, new Date(validDraws[0].cutoff || validDraws[0].draw_date));

  const now = new Date();
  const timeUntilExpiry = earliestCutoff.getTime() - now.getTime();

  if (timeUntilExpiry > 0) {
    if (drawsRefreshTimer) {
      clearTimeout(drawsRefreshTimer);
    }

    drawsRefreshTimer = window.setTimeout(() => {
      gameStore.refreshDraws().then(() => {
        if (countdownTimer) {
          clearInterval(countdownTimer);
        }
        updateCountdown();
        countdownTimer = window.setInterval(updateCountdown, 1000);
        setupDrawRefreshTimer();
      });
    }, timeUntilExpiry + 1000);
  }
};

onMounted(async () => {
  window.addEventListener("resize", handleResize);
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
  setupDrawRefreshTimer();
});

watch(() => route.path, () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
});

watch(() => gameStore.getValidDraws, (newDraws, oldDraws) => {
  if (newDraws.length !== oldDraws?.length) {
    const newSelection = new Array(newDraws.length).fill(false);
    
    if (oldDraws && oldDraws.length > 0) {
      oldDraws.forEach((oldDraw: { event_id: string | number }, oldIndex: number) => {
        if (selectedDrawsArray.value[oldIndex]) {
          const matchingIndex = newDraws.findIndex((newDraw: { event_id: string | number }) => 
            newDraw.event_id === oldDraw.event_id
          );
          if (matchingIndex !== -1) {
            newSelection[matchingIndex] = true;
          }
        }
      });
    }
    
    if (!newSelection.some(Boolean) && newDraws.length > 0) {
      newSelection[0] = true;
    }
    
    selectedDrawsArray.value = newSelection;
    setupDrawRefreshTimer();
  }
}, { immediate: true });

watch(() => betslip.value.length, (newLength, oldLength) => {
  if (oldLength && oldLength > 0 && newLength === 0) {
    if (!isClearingAfterSubmission.value) {
      selectedMarket.value = null;
      mobileActiveTab.value = 'main';
    }
    isClearingAfterSubmission.value = false;
  }
});

watch(() => modalStore.showBetslip, (isOpen) => {
  if (!isOpen && isMobile.value) {
    mobileActiveTab.value = 'main';
    lines.value = [];
  }
});

let resizeTimeout: number | null = null;
const handleResize = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = window.setTimeout(() => {
    const newIsMobile = window.innerWidth <= 768;
    if (newIsMobile !== isMobile.value) {
      selectedMarket.value = null;
      lines.value = [];
      mobileActiveTab.value = 'main';
      isMobile.value = newIsMobile;
    }
  }, 100);
};

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }
  if (countdownTimer) {
    clearInterval(countdownTimer);
  }
  if (drawsRefreshTimer) {
    clearTimeout(drawsRefreshTimer);
  }
});


defineExpose({
  selectDraws
});
</script>

<style scoped>
/* Custom Header Styles */

.view-bets-container {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  width: 100%;
  padding: 16px;
  background: var(--bg-dark);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.view-bets {
  width: 100%;
  padding: 16px;
  background: var(--bg-danger);
  color: var(--text-white);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.2s;
}

.view-bets:hover {
  background: var(--bg-danger-hover);
}

.detail-view {
  max-width: 100%;
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.add-to-betslip.processing {
  position: relative;
  pointer-events: none;
  background: var(--bg-dark-tertiary);
}

.add-to-betslip.processing::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border: 2px solid var(--text-white);
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
/* Base Layout Styles */
.content-layout {
  display: flex;
  gap: 20px;
  padding: 20px 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.main-content-wrapper {
  flex: 1;
  max-width: 70%;
  min-width: 0;
  box-sizing: border-box;
}

.betslip-wrapper {
  width: 30%;
  min-width: 300px;
  max-width: 400px;
  flex-shrink: 0;
  box-sizing: border-box;
}

/* Custom styles specific to DetailView */
.lottery-card-wrapper {
  flex: 1;
  max-width: 80%;
}

.lottery-card-wrapper :deep(.lottery-card) {
  height: 100%;
  margin: 0;
}

.back-button {
  border-radius: 50px;
  font-size: 16px;
  font-weight: 600;
  padding: 32px;
  margin-left: 24px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

@media (max-width: 600px) {
  .back-button {
    width: 100%;
    order: -1;
    margin-bottom: 12px;
    border-radius: 8px;
    background: var(--lottery-card-gradient);
    box-shadow: 0 2px 8px var(--shadow-light);
  }
  .lottery-card-wrapper {
    width: 100%;
  }
}

/* Desktop Draws Section */
.desktop-draws-section {
  background: var(--bg-view-header, var(--bg-secondary));
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.desktop-draws-header {
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.desktop-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.desktop-draws-content {
  padding: 12px 16px;
}

.desktop-draws-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.desktop-draw-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: var(--bg-bet-check-box, var(--bg-primary));
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.desktop-draw-item:hover {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.desktop-draw-item.selected {
  border-color: var(--accent-color);
  background: var(--bg-hover);
}

.desktop-draw-content {
  display: flex;
  align-items: center;
  gap: 6px;
}

.desktop-time-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
}

.desktop-time-icon img {
  width: 14px;
  height: 14px;
  filter: brightness(0) saturate(100%) invert(20%) sepia(8%) saturate(1038%) hue-rotate(169deg) brightness(95%) contrast(86%);
}

.desktop-draw-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.desktop-draw-date {
  font-size: 11px;
  color: var(--text-muted);
  font-weight: 400;
}

.desktop-draw-time {
  font-size: 11px;
  color: var(--text-accent);
  font-weight: 600;
}

.desktop-checkbox-wrapper {
  position: relative;
  display: inline-block;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.desktop-checkbox-wrapper input {
  opacity: 0;
  width: 0;
  height: 0;
}

.desktop-checkmark {
  position: absolute;
  top: 0;
  left: 0;
  width: 14px;
  height: 14px;
  background: var(--text-white);
  border: 2px solid var(--border-color);
  border-radius: 2px;
  transition: all 0.2s ease;
}

.desktop-checkbox-wrapper input:checked ~ .desktop-checkmark {
  background: var(--accent-color);
  border-color: var(--accent-color);
}

.desktop-checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.desktop-checkbox-wrapper input:checked ~ .desktop-checkmark:after {
  display: block;
}

.desktop-checkbox-wrapper .desktop-checkmark:after {
  left: 3px;
  top: 0px;
  width: 2px;
  height: 5px;
  border: solid white;
  border-width: 0 1.5px 1.5px 0;
  transform: rotate(45deg);
}

.betting-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

@media (max-width: 768px) {
  .betting-section {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
}

.number-selection-container {
  padding: 0;
  position: relative;
}

.selection-header {
  margin-bottom: 24px;
}

.add-to-betslip {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-success);
  color: var(--text-white);
  border: none;
  padding: 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.2s;
  z-index: 100;
}

.add-to-betslip:hover {
  background: var(--bg-success-hover);
}

.add-to-betslip:disabled {
  background: var(--bg-dark-tertiary);
  cursor: not-allowed;
  opacity: 0.5;
}

.back-button {
  background: transparent;
  border: 2px solid var(--bg-danger);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 16px;
  padding: 8px 16px;
  border-radius: 8px;
  transition: color 0.2s;
}

.back-button:hover {
  color: var(--bg-danger);
}

.betting-group {
  background: var(--bg-dark-tertiary);
  border-radius: 12px;
  padding: 12px 16px;
}

.betting-title {
  color: var(--text-primary);
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 8px;
}

.subtitle {
  color: var(--text-light);
  font-size: 14px;
  margin-bottom: 24px;
}

.betting-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.sum-options {
  grid-template-columns: repeat(2, 1fr);
}

.bet-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--bg-hover);
  padding: 12px 16px;
  border-radius: 8px;
}

.bet-label {
  color: var(--text-primary);
  font-size: 14px;
}

.bet-amount {
  color: var(--text-primary);
  font-weight: 500;
  font-size: 14px;
}
/* Medium screens (tablets) - Keep grid approach */
@media (max-width: 1024px) and (min-width: 769px) {
  .content-layout {
    display: flex;
    gap: 16px;
    padding: 16px 0px;
  }
  
  .main-content-wrapper {
    flex: 1;
    max-width: 60%;
  }
  
  .betslip-wrapper {
    width: 40%;
    min-width: 300px;
    max-width: 400px;
    overflow: hidden;
  }
  
  .draws-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }
  
  .draws-grid::before {
    left: calc(33.333% + 5px);
  }
  
  .draws-grid::after {
    left: calc(66.666% + 5px);
  }
  
  
  .betting-options {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .sum-options {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .number-selection-container {
    width: 100%;
    max-width: 100%;
    margin: 0;
    padding: 16px;
  }
  
  .selection-header {
    margin-bottom: 16px;
  }
  
  .selection-header .back-button {
    padding: 12px 24px;
    font-size: 14px;
    margin-left: 0;
  }
}

/* Smaller medium screens - Adjust grid proportions */
@media (max-width: 900px) and (min-width: 769px) {
  .main-content-wrapper {
    max-width: 55%;
  }
  
  .betslip-wrapper {
    width: 45%;
    min-width: 280px;
    max-width: 350px;
    overflow: hidden;
  }
  
  .draws-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }
  
  .draws-grid::before {
    left: calc(33.333% + 4px);
  }
  
  .draws-grid::after {
    left: calc(66.666% + 4px);
  }
  
}

/* Large tablets and small desktops */
@media (max-width: 1200px) and (min-width: 1025px) {
  /* .content-layout {
    gap: 20px;
  }
   */
  .main-content-wrapper {
    max-width: 65%;
  }
  
  .betslip-wrapper {
    width: 35%;
    min-width: 280px;
    max-width: 320px;
    overflow: hidden;
  }
  
  .draws-grid {
    grid-template-columns: repeat(3, 1fr);
  }
  
  .draws-grid::before {
    left: calc(33.333% + 5px);
  }
  
  .draws-grid::after {
    left: calc(66.666% + 5px);
  }
  
}

@media (max-width: 768px) {
  .betslip-wrapper {
    display: none !important;
  }
  
  .content-layout {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }
  
  .main-content-wrapper {
    width: 100%;
    max-width: 100%;
    padding: 0 16px 80px 16px;
    box-sizing: border-box;
  }
  
  .draws-section {
    margin-bottom: 24px;
  }
  
  .draws-container {
    margin-top: 12px;
  }
  
  .draws-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 4px;
  }
  
  .draws-grid::before,
  .draws-grid::after {
    display: none;
  }
  
  .draw-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: var(--bg-tertiary);
    border-radius: 6px;
    border: 1px solid var(--border-light);
    cursor: pointer;
  }
  
  .draw-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .time-icon img {
    width: 16px;
    height: 16px;
    filter: brightness(0) saturate(100%) invert(20%) sepia(8%) saturate(1038%) hue-rotate(169deg) brightness(95%) contrast(86%);
  }
  
  .draw-info {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .date {
    font-size: 12px;
    color: var(--text-muted);
    font-weight: 400;
  }
  
  .time {
    font-size: 12px;
    color: var(--text-accent);
    font-weight: 600;
  }
  
  .checkbox-wrapper {
    width: 16px;
    height: 16px;
  }
  
  .checkmark {
    width: 16px;
    height: 16px;
    border: 2px solid var(--border-dark);
    border-radius: 3px;
  }
  
  .checkbox-wrapper input:checked ~ .checkmark {
    background: var(--border-dark);
    border-color: var(--border-dark);
  }
  
  .checkbox-wrapper .checkmark:after {
    left: 4px;
    top: 1px;
    width: 3px;
    height: 6px;
    border: solid white;
    border-width: 0 1.5px 1.5px 0;
    transform: rotate(45deg);
  }
  
  .betting-section {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    max-width: 100%;
  }
  
  .betting-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  
  .number-selection-container {
    width: 100%;
    max-width: 100%;
    margin: 16px 0 0 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  .selection-header {
    font-size: 16px;
    padding: 0 0 12px 0;
  }
  
  .number-grid {
    grid-template-columns: repeat(6, 1fr);
    gap: 8px;
  }
  
  .number-grid > * {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 8px;
    margin-top: 16px;
  }
  
  .quick-hot-cold-row {
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
  }
  
  .add-line-btn {
    width: 100%;
    padding: 12px;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .main-content-wrapper {
    padding: 0 6px;
  }
  
  .draws-header {
    padding: 0 6px;
  }
  
  .betting-section {
    gap: 8px;
  }
  
  .number-selection-container {
    margin: 12px 0 0 0;
  }
}

.draws-header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 0 10px;
}


  .collapse-btn {
    background: none;
    border: none;
    color: var(--text-white);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

/* Mobile View Bets Button */

.mobile-view-bets {
  width: 100%;
  margin-top: 16px;
  padding: 16px;
  background: var(--bg-danger);
  color: var(--text-white);
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: background-color 0.2s;
}

.mobile-view-bets:hover {
  background: var(--bg-danger-hover);
}

/* Mobile Back Button */
.mobile-back-container {
  margin-top: 12px;
}

.mobile-back-button {
  width: 100%;
  padding: 16px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.mobile-back-button:hover {
  background: var(--bg-hover);
  border-color: var(--accent-color);
}

.mobile-back-icon {
  width: 20px;
  height: 20px;
}

.mobile-back-text {
  font-size: 14px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .mobile-view-bets-container {
    display: block;
  }
}

.related-games-inline-wrapper {
  width: 100%;
  margin: 10px 0;
  padding: 16px 20px;
  background: var(--bg-secondary);
  border-radius: 12px;
  border: 1px solid var(--border-color);
  box-sizing: border-box;
}

.related-games-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
}

.related-games-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
</style>